/**
 * Micro-celebrations — state-change delight, GPU-only.
 *
 *   - `popPulse(el)`   overshooting scale pulse (like → pop, badge bump).
 *   - `burstFrom(el)`  a small radial particle burst anchored at the element
 *     (add-to-collection → celebration). Particles are positioned ONCE at
 *     spawn (static left/top) and then animated exclusively with
 *     transform/opacity keyframes on their own layer; the overlay is inert
 *     (pointer-events: none) and self-removes.
 *
 * Reduced motion: `popPulse` relies on the global CSS guard (duration ~0);
 * `burstFrom` skips entirely — decorative particles have no reduced fallback.
 */

import { vibrate } from "@/lib/gestures/haptics";

import { prefersReducedMotion } from "./reduced-motion";

const POP_CLASS = "motion-pop";
const BURST_LIFETIME_MS = 750;
const BURST_PARTICLES = 12;
const BURST_MIN_RADIUS_PX = 36;
const BURST_MAX_RADIUS_PX = 84;

/** Replayable scale pulse. Re-adding the class mid-flight restarts it. */
export function popPulse(el: HTMLElement, options: { hapticMs?: number } = {}): void {
  if (options.hapticMs) {
    vibrate(options.hapticMs);
  }
  el.classList.remove(POP_CLASS);
  // Force style resolution so re-adding the class restarts the animation.
  void el.offsetWidth;
  el.classList.add(POP_CLASS);
  const onEnd = (event: AnimationEvent) => {
    if (event.target !== el) {
      return;
    }
    el.classList.remove(POP_CLASS);
    el.removeEventListener("animationend", onEnd);
  };
  el.addEventListener("animationend", onEnd);
}

/**
 * Radial particle burst centred on `el`. Fire-and-forget: appends a fixed,
 * inert overlay to <body> and removes it after the animation. Survives the
 * source element unmounting (e.g. immediate navigation after adding).
 */
export function burstFrom(el: HTMLElement, options: { hapticMs?: number } = {}): void {
  if (options.hapticMs) {
    vibrate(options.hapticMs);
  }
  if (prefersReducedMotion() || typeof document === "undefined") {
    return;
  }

  const rect = el.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const overlay = document.createElement("div");
  overlay.className = "motion-burst-overlay";
  overlay.setAttribute("aria-hidden", "true");
  // Positioned once at spawn — never animated, so no layout work per frame.
  overlay.style.left = `${cx}px`;
  overlay.style.top = `${cy}px`;

  for (let i = 0; i < BURST_PARTICLES; i += 1) {
    const particle = document.createElement("span");
    particle.className = "motion-burst-particle";
    const angle = (Math.PI * 2 * i) / BURST_PARTICLES + Math.random() * 0.6;
    const radius =
      BURST_MIN_RADIUS_PX + Math.random() * (BURST_MAX_RADIUS_PX - BURST_MIN_RADIUS_PX);
    particle.style.setProperty("--burst-x", `${Math.cos(angle) * radius}px`);
    particle.style.setProperty("--burst-y", `${Math.sin(angle) * radius}px`);
    particle.style.setProperty("--burst-delay", `${Math.random() * 60}ms`);
    overlay.append(particle);
  }

  document.body.append(overlay);
  setTimeout(() => {
    overlay.remove();
  }, BURST_LIFETIME_MS);
}
